import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SharedMessagePacketsPanel } from '../inbox/SharedMessagePacketsPanel'
import {
  ClockIcon,
  PlayIcon,
  SquareIcon,
  CheckCircleIcon,
  ClipboardListIcon,
  MapPinIcon,
  MapIcon,
  ListIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  Loader2Icon,
  CalendarIcon,
} from 'lucide-react'
import {
  MobilePage,
  MobileExpandable,
  MobileEmptyState,
  MobileStatStrip,
  MobileStatusBadge,
  mobile,
} from '../mobile'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import {
  clockIn,
  dayPunchInNotesForJob,
  fetchEmployeeClockState,
  findOpenInProgressJobForEmployee,
  tenantRequiresFinishJobBeforeStart,
  logDayPrompt,
  punchOutForDay,
  respondDayPrompt,
  resolveEmployeeIdForUser,
  startJobWorkflow,
  enterTravelAfterJob,
  travelMinutesOpen,
  closeStaleJobTimeEntry,
  FieldGpsError,
  type EmployeeClockState,
  type OpenInProgressJob,
} from '../../lib/timeClockActions'
import { UnfinishedJobClockOutModal } from './UnfinishedJobClockOutModal'
import { PunchInJobPickerSheet } from './PunchInJobPickerSheet'
import { jobCompleteGpsPatch } from '../../lib/geolocation'
import { gateJobSiteAction, requireGpsCapture } from '../../lib/fieldGpsGate'
import { isDsnyEveningJob, optimizeRouteOrder } from '../../lib/routeOptimizer'
import { evaluateJobCompletionGate, isJobChecklistComplete } from '../../lib/jobCompletionGate'
import { getAppLocale } from '../../i18n'
import { formatFieldDate, intlLocale } from '../../i18n/localeFormat'
import { resolvePostCompletionStatus } from '../../lib/jobStatusWorkflow'
import { stampJobHoursActualFromTimeEntries } from '../../lib/jobActualHours'
import { usesFieldHome } from '../../lib/fieldHome'
import { useAppSettings } from '../../hooks/useAppSettings'
import { JobTaskChecklist } from '../job/JobTaskChecklist'
import { ApartmentJobWork } from '../job/ApartmentJobWork'
import { JobCompletionModal } from '../job/JobCompletionModal'
import { FieldJobCompletionModal } from '../job/FieldJobCompletionModal'
import { CompletionExceptionModal } from '../job/CompletionExceptionModal'
import { usePermissions } from '../../hooks/usePermissions'
import {
  canCompleteJobWithException,
  completionExceptionUpdate,
} from '../../lib/jobCompletionException'
import { addCalendarDays, formatDayNavLabel, todayDateInputValue } from '../../lib/dateUtils'
import { resolveAssignedJobsScope } from '../../lib/jobVisibility'
import { fetchJobTasks } from '../../api/jobWorkflow'
import { countQueuedPhotosForJob } from '../../lib/offlinePhotoQueue'
import {
  cacheMyDayClock,
  cacheMyDayJobs,
  clearMyDayJobsCache,
  readCachedEmployeeId,
  readCachedMyDayClock,
  purgeLegacyMyDayJobCaches,
  readCachedMyDayJobs,
} from '../../lib/offlineMyDayCache'
import {
  isJobDoneStatus,
  isMyDayJobClockPaused,
  MY_DAY_TRAVEL_IDLE_MINUTES,
  resolveMyDayWorkflowButton,
} from '../../lib/myDayJobWorkflow'
import { JobsDayMap, jobLatLng } from './JobsDayMap'

interface MyDayJob {
  id: string
  job_number: string
  title: string
  status: string
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  contract_id?: string | null
  service_type?: string | null
  dsny_integration?: boolean | null
  dsny_pickup_days?: string[] | null
  customer?: {
    company_name?: string | null
    contact_first_name?: string | null
    contact_last_name?: string | null
  } | null
  property?: {
    name?: string | null
    address?: string | null
    latitude?: number | null
    longitude?: number | null
  } | null
}

type JobsViewMode = 'list' | 'map'

export interface MyDayContentProps {
  title?: string
  subtitle?: string
  showHeader?: boolean
  /** Skip MobilePage wrapper when nested inside another page shell. */
  embedded?: boolean
  /** Optional content between header and clock/jobs (e.g. quick links). */
  topSlot?: React.ReactNode
}

type JobStatusFilter = 'total' | 'yet' | 'progress' | 'done'

function jobMatchesStatusFilter(job: MyDayJob, filter: JobStatusFilter): boolean {
  if (filter === 'total') return true
  if (filter === 'progress') return job.status === 'in_progress'
  if (filter === 'done') return isJobDoneStatus(job.status)
  return (
    !isJobDoneStatus(job.status) &&
    job.status !== 'in_progress' &&
    job.status !== 'cancelled'
  )
}

function inProgressStripLabel(job: MyDayJob): string {
  return (job.title || '').trim() || job.property?.name || job.property?.address || 'Job'
}

function MyDayInProgressStrip({
  job,
  busy,
  disabled,
  open,
  onToggle,
  onOpenDetails,
  onComplete,
  children,
}: {
  job: MyDayJob
  busy: boolean
  disabled: boolean
  open: boolean
  onToggle: () => void
  onOpenDetails: () => void
  onComplete: () => void
  children: React.ReactNode
}) {
  const { t } = useTranslation('field')
  const name = inProgressStripLabel(job)
  return (
    <div>
      <p className={`${mobile.sectionLabel} mb-1.5`}>{t('jobInProgress')}</p>
      <div className={`${mobile.surface} overflow-hidden border-l-[3px] border-l-sky-500`}>
        <div className="flex items-center gap-2 px-3 py-1.5 min-h-[44px]">
          <button
            type="button"
            onClick={onOpenDetails}
            className="flex-1 min-w-0 text-left rounded-lg py-1 -my-1 active:bg-gray-50/80"
          >
            <span className="block text-sm font-semibold text-gray-900 truncate">{name}</span>
          </button>
          <button
            type="button"
            disabled={busy || disabled}
            onClick={(e) => {
              e.stopPropagation()
              onComplete()
            }}
            className="inline-flex items-center justify-center gap-1 min-h-[36px] px-3 text-xs font-semibold rounded-full text-white flex-shrink-0 whitespace-nowrap bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? (
              <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircleIcon className="w-3.5 h-3.5" />
            )}
            {t('workflow.complete')}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onToggle()
            }}
            className={`${mobile.ghostBtn} flex-shrink-0 min-h-[36px] min-w-[36px]`}
            aria-expanded={open}
            aria-label={open ? t('collapseChecklist') : t('expandChecklist')}
          >
            {open ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
          </button>
        </div>
        <div
          className={`border-t border-gray-100 bg-gray-50/80 p-4 space-y-4 ${open ? '' : 'hidden'}`}
          aria-hidden={!open}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

const emptyClock: EmployeeClockState = {
  dayEntry: null,
  jobEntry: null,
  travelEntry: null,
}

export function MyDayContent({
  title,
  subtitle,
  showHeader = true,
  embedded = false,
  topSlot,
}: MyDayContentProps) {
  const { t } = useTranslation('field')
  const locale = getAppLocale()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { canSeePricing, profile } = usePermissions()
  const fieldHome = usesFieldHome(profile?.role)
  const canExceptionComplete = canCompleteJobWithException(profile?.role)
  const { photoPolicy } = useAppSettings()
  const [exceptionJobId, setExceptionJobId] = useState<string | null>(null)
  const [exceptionGateMessage, setExceptionGateMessage] = useState<string | null>(null)

  const [employeeId, setEmployeeId] = useState<string | null>(null)
  const [employeeLoading, setEmployeeLoading] = useState(true)
  const [jobs, setJobs] = useState<MyDayJob[]>([])
  const [todayJobs, setTodayJobs] = useState<MyDayJob[]>([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [jobsView, setJobsView] = useState<JobsViewMode>('list')
  const [routeBusy, setRouteBusy] = useState(false)
  const [clock, setClock] = useState<EmployeeClockState>(emptyClock)
  const [busyJobId, setBusyJobId] = useState<string | null>(null)
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null)
  const [stripChecklistOpen, setStripChecklistOpen] = useState(false)
  const [completionJobId, setCompletionJobId] = useState<string | null>(null)
  const [punchBusy, setPunchBusy] = useState(false)
  const [statusFilter, setStatusFilter] = useState<JobStatusFilter>('total')
  const [checklistCompleteByJob, setChecklistCompleteByJob] = useState<Record<string, boolean>>({})
  const [photoModeNonceByJob, setPhotoModeNonceByJob] = useState<Record<string, number>>({})
  const [blockingJob, setBlockingJob] = useState<OpenInProgressJob | null>(null)
  const [requireFinishBeforeStart, setRequireFinishBeforeStart] = useState(false)
  const [clockOutPromptJob, setClockOutPromptJob] = useState<OpenInProgressJob | null>(null)
  const [punchInPickerOpen, setPunchInPickerOpen] = useState(false)

  const idlePromptedTravelId = useRef<string | null>(null)
  const lastJobPrompted = useRef(false)
  const promptBusy = useRef(false)

  const todayYmd = todayDateInputValue()
  const [selectedDate, setSelectedDate] = useState(todayYmd)
  const isViewingToday = selectedDate === todayYmd
  const dayNavLabel = formatDayNavLabel(selectedDate, todayYmd, {
    locale,
    todayLabel: t('today'),
  })
  const displayTitle = title ?? t('myDay')
  const todayLabel =
    subtitle ??
    formatFieldDate(new Date(), locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  const naLabel = t('na', { ns: 'common' })

  useEffect(() => {
    purgeLegacyMyDayJobCaches()
  }, [])

  useEffect(() => {
    void tenantRequiresFinishJobBeforeStart().then(setRequireFinishBeforeStart)
  }, [])

  useEffect(() => {
    const resolveEmployee = async () => {
      if (!user?.id) {
        setEmployeeLoading(false)
        return
      }
      const cachedEmpId = readCachedEmployeeId(user.id)
      if (cachedEmpId) setEmployeeId(cachedEmpId)
      try {
        const empId = await resolveEmployeeIdForUser(user.id)
        setEmployeeId(empId)
      } catch (err) {
        console.error('Error resolving employee for user:', err)
        if (cachedEmpId) setEmployeeId(cachedEmpId)
      } finally {
        setEmployeeLoading(false)
      }
    }
    resolveEmployee()
  }, [user?.id])

  const loadJobs = useCallback(
    async (empId: string, dateYmd: string) => {
      try {
        setJobsLoading(true)
        const jobSelect = `id, job_number, title, status, scheduled_start_time, scheduled_end_time, contract_id, service_type,
           dsny_integration, dsny_pickup_days,
           customer:customers(company_name, contact_first_name, contact_last_name),
           property:properties(name, address, latitude, longitude)`
        const { orFilter, extraJobIds } = await resolveAssignedJobsScope(empId, {
          scheduledDate: dateYmd,
        })
        const { data, error } = await supabase
          .from('jobs')
          .select(jobSelect)
          .eq('scheduled_date', dateYmd)
          .or(orFilter)
          .order('scheduled_start_time', { ascending: true })

        if (error) throw error

        const byId = new Map<string, any>()
        for (const row of data || []) byId.set(row.id, row)

        if (extraJobIds.length > 0) {
          for (let i = 0; i < extraJobIds.length; i += 80) {
            const chunk = extraJobIds.slice(i, i + 80)
            const { data: extraRows, error: extraErr } = await supabase
              .from('jobs')
              .select(jobSelect)
              .eq('scheduled_date', dateYmd)
              .in('id', chunk)
            if (extraErr) throw extraErr
            for (const row of extraRows || []) byId.set(row.id, row)
          }
        }

        const merged = Array.from(byId.values())
          .map((row: any) => ({
            ...row,
            customer: Array.isArray(row.customer) ? row.customer[0] || null : row.customer,
            property: Array.isArray(row.property) ? row.property[0] || null : row.property,
          }))
          .sort((a, b) =>
            String(a.scheduled_start_time || '').localeCompare(String(b.scheduled_start_time || ''))
          )
        setJobs(merged)
        if (dateYmd === todayDateInputValue()) setTodayJobs(merged)
        if (merged.length > 0) {
          cacheMyDayJobs(empId, dateYmd, merged as MyDayJob[])
        } else {
          clearMyDayJobsCache(empId, dateYmd)
        }
        return merged as MyDayJob[]
      } catch (err) {
        console.error('Error loading My Day jobs:', err)
        const online = typeof navigator !== 'undefined' ? navigator.onLine : true
        if (!online) {
          const cached = readCachedMyDayJobs(empId, dateYmd)
          if (cached?.length) {
          setJobs(cached)
          if (dateYmd === todayDateInputValue()) setTodayJobs(cached)
          return cached as MyDayJob[]
          }
        }
        return [] as MyDayJob[]
      } finally {
        setJobsLoading(false)
        setHasLoaded(true)
      }
    },
    []
  )

  const refreshBlockingJob = useCallback(async (empId: string) => {
    try {
      const open = await findOpenInProgressJobForEmployee(empId)
      setBlockingJob(open)
      return open
    } catch (err) {
      console.error('Error loading in-progress job:', err)
      setBlockingJob(null)
      return null
    }
  }, [])

  const loadClock = useCallback(async (empId: string) => {
    try {
      const [state] = await Promise.all([fetchEmployeeClockState(empId), refreshBlockingJob(empId)])
      if (state.jobEntry?.job_id) {
        const { data: jobRow } = await supabase
          .from('jobs')
          .select('status')
          .eq('id', state.jobEntry.job_id)
          .maybeSingle()
        if (jobRow && isJobDoneStatus(jobRow.status)) {
          try {
            await closeStaleJobTimeEntry(state.jobEntry)
          } catch {
            // Still clear locally so finished jobs cannot grey out Start.
          }
          const next = await fetchEmployeeClockState(empId)
          const cleared = { ...next, jobEntry: null }
          setClock(cleared)
          cacheMyDayClock(empId, cleared)
          return cleared
        }
      }
      setClock(state)
      cacheMyDayClock(empId, state)
      return state
    } catch (err) {
      console.error('Error loading clock state:', err)
      const cached = readCachedMyDayClock(empId)
      if (cached) {
        setClock(cached)
        return cached
      }
      return emptyClock
    }
  }, [refreshBlockingJob])

  const refreshChecklistForJobs = useCallback(async (jobList: MyDayJob[]) => {
    const inProgress = jobList.filter((j) => j.status === 'in_progress')
    if (inProgress.length === 0) {
      setChecklistCompleteByJob({})
      return
    }
    const next: Record<string, boolean> = {}
    await Promise.all(
      inProgress.map(async (j) => {
        try {
          const tasks = await fetchJobTasks(j.id)
          next[j.id] = isJobChecklistComplete(tasks)
        } catch {
          next[j.id] = false
        }
      })
    )
    setChecklistCompleteByJob(next)
  }, [])

  useEffect(() => {
    if (employeeId) {
      ;(async () => {
        const [jobList] = await Promise.all([
          loadJobs(employeeId, selectedDate),
          loadClock(employeeId),
        ])
        void refreshChecklistForJobs(jobList)
      })()
    } else if (!employeeLoading) {
      setJobsLoading(false)
      setHasLoaded(true)
    }
  }, [employeeId, employeeLoading, selectedDate, loadJobs, loadClock, refreshChecklistForJobs])

  useEffect(() => {
    setJobsView('list')
  }, [selectedDate])

  const openJobsRemaining = todayJobs.filter(
    (j) => !isJobDoneStatus(j.status) && j.status !== 'cancelled'
  )
  const allJobsDone =
    todayJobs.length > 0 &&
    todayJobs.every((j) => isJobDoneStatus(j.status) || j.status === 'cancelled')

  const askPunchOutForDay = useCallback(
    async (opts: {
      promptType: 'idle_30min' | 'last_job_done'
      message: string
      asOfPreviousJob: boolean
      relatedJobId?: string | null
    }) => {
      if (!employeeId || promptBusy.current) return
      promptBusy.current = true
      try {
        const state = await fetchEmployeeClockState(employeeId)
        if (!state.dayEntry) return

        const promptId = await logDayPrompt({
          employeeId,
          promptType: opts.promptType,
          relatedJobId: opts.relatedJobId,
          dayEntryId: state.dayEntry.id,
        })

        const yes = window.confirm(opts.message)
        if (yes) {
          const asOf =
            opts.asOfPreviousJob && state.travelEntry
              ? state.travelEntry.clock_in_time
              : opts.asOfPreviousJob
                ? new Date().toISOString()
                : null
          await punchOutForDay({
            employeeId,
            asOf: opts.asOfPreviousJob ? asOf : null,
          })
          await respondDayPrompt({
            promptId,
            response: 'done_for_day',
            autoPunchedOut: false,
          })
          setClock(emptyClock)
          idlePromptedTravelId.current = null
          await refreshBlockingJob(employeeId)
        } else {
          await respondDayPrompt({ promptId, response: 'still_working' })
        }
      } catch (err: any) {
        if (err instanceof FieldGpsError) {
          if (err.code !== 'cancelled') alert(err.message)
          return
        }
        alert(t('punchUpdateFailed', { message: err.message || t('unknownError', { ns: 'common' }) }))
      } finally {
        promptBusy.current = false
      }
    },
    [employeeId, refreshBlockingJob]
  )

  const maybePromptTravelIdle = useCallback(
    async (state?: EmployeeClockState) => {
      if (!employeeId) return
      const clockState = state || clock
      if (!clockState.travelEntry || !clockState.dayEntry) return
      if (allJobsDone) return
      if (openJobsRemaining.length === 0) return
      if (idlePromptedTravelId.current === clockState.travelEntry.id) return

      const mins = travelMinutesOpen(clockState.travelEntry)
      if (mins < MY_DAY_TRAVEL_IDLE_MINUTES) return

      idlePromptedTravelId.current = clockState.travelEntry.id
      await askPunchOutForDay({
        promptType: 'idle_30min',
        message:
          t('travelIdlePrompt'),
        asOfPreviousJob: true,
      })
    },
    [employeeId, clock, allJobsDone, openJobsRemaining.length, askPunchOutForDay]
  )

  const maybePromptAllJobsDone = useCallback(
    async (state?: EmployeeClockState, forceAllDone?: boolean) => {
      if (!employeeId || lastJobPrompted.current) return
      const clockState = state || clock
      if (!clockState.dayEntry) return
      if (!(forceAllDone || allJobsDone)) return

      lastJobPrompted.current = true
      await askPunchOutForDay({
        promptType: 'last_job_done',
        message: t('allJobsDonePunchOut'),
        asOfPreviousJob: !!clockState.travelEntry,
      })
    },
    [employeeId, clock, allJobsDone, askPunchOutForDay]
  )

  // Travel idle + all-done prompts on load / focus / interval
  useEffect(() => {
    if (!employeeId || jobsLoading) return

    const run = () => {
      void maybePromptTravelIdle()
      void maybePromptAllJobsDone()
    }
    run()

    const onFocus = () => {
      void loadClock(employeeId).then((state) => {
        void maybePromptTravelIdle(state)
        void maybePromptAllJobsDone(state)
      })
    }
    window.addEventListener('focus', onFocus)
    const interval = window.setInterval(() => {
      void loadClock(employeeId).then((state) => {
        void maybePromptTravelIdle(state)
      })
    }, 60000)

    return () => {
      window.removeEventListener('focus', onFocus)
      window.clearInterval(interval)
    }
  }, [
    employeeId,
    jobsLoading,
    clock.travelEntry?.id,
    allJobsDone,
    maybePromptTravelIdle,
    maybePromptAllJobsDone,
    loadClock,
  ])

  useEffect(() => {
    if (!clock.travelEntry) {
      idlePromptedTravelId.current = null
    }
  }, [clock.travelEntry?.id])

  const getCustomerName = (job: MyDayJob) =>
    job.customer?.company_name ||
    `${job.customer?.contact_first_name || ''} ${job.customer?.contact_last_name || ''}`.trim() ||
    naLabel

  const formatTime = (time?: string | null) => {
    if (!time) return naLabel
    return new Date(`2000-01-01T${time}`).toLocaleTimeString(intlLocale(locale), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  const handleStart = async (job: MyDayJob) => {
    if (!employeeId) return
    try {
      setBusyJobId(job.id)
      const { state } = await startJobWorkflow({
        employeeId,
        jobId: job.id,
        currentStatus: job.status,
      })
      setClock(state)
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, status: 'in_progress' } : j)))
      setBlockingJob({
        id: job.id,
        job_number: job.job_number,
        title: job.title,
      })
      setExpandedJobId(job.id)
      setStripChecklistOpen(true)
      setPhotoModeNonceByJob((prev) => ({
        ...prev,
        [job.id]: (prev[job.id] || 0) + 1,
      }))
      await refreshChecklistForJobs(
        jobs.map((j) => (j.id === job.id ? { ...j, status: 'in_progress' } : j))
      )
    } catch (err: any) {
      if (err instanceof FieldGpsError) {
        if (err.code !== 'cancelled') alert(err.message)
        return
      }
      alert(t('startJobFailed', { message: err.message || t('unknownError', { ns: 'common' }) }))
    } finally {
      setBusyJobId(null)
    }
  }

  const doClockOutDay = async () => {
    if (!employeeId) return
    setPunchBusy(true)
    try {
      // Manual header punch-out uses "now" (idle-travel prompt uses as-of last job).
      await punchOutForDay({ employeeId, asOf: null })
      setClock(emptyClock)
      await refreshBlockingJob(employeeId)
    } catch (err: any) {
      if (err instanceof FieldGpsError) {
        if (err.code !== 'cancelled') alert(err.message)
        return
      }
      alert(t('clockOutFailed', { message: err.message || t('unknownError', { ns: 'common' }) }))
    } finally {
      setPunchBusy(false)
      setClockOutPromptJob(null)
    }
  }

  const handleClockOutDay = async () => {
    if (!employeeId || !clock.dayEntry) return
    const unfinished =
      blockingJob ||
      (await refreshBlockingJob(employeeId)) ||
      jobs
        .filter((j) => j.status === 'in_progress')
        .map((j) => ({ id: j.id, job_number: j.job_number, title: j.title }))[0] ||
      null
    if (unfinished) {
      setClockOutPromptJob(unfinished)
      return
    }
    await doClockOutDay()
  }

  /** Greeting-card Punch In: day clock only Ã¢â‚¬â€ pick a job or Other. Does not start a job. */
  const handlePunchIn = () => {
    if (!employeeId || clock.dayEntry) return
    setPunchInPickerOpen(true)
  }

  const confirmDayPunchIn = async (opts: {
    job?: { id: string; job_number: string; title: string }
    otherNotes?: string
  }) => {
    if (!employeeId || clock.dayEntry) return
    const notes = opts.job
      ? dayPunchInNotesForJob(opts.job)
      : (opts.otherNotes || '').trim()
    if (!notes) {
      alert(t('punchInDescribe'))
      return
    }
    setPunchBusy(true)
    setPunchInPickerOpen(false)
    try {
      const { entry } = await clockIn({
        employeeId,
        entryType: 'day',
        notes,
        geofenceJobId: opts.job?.id || null,
      })
      setClock((prev) => ({ ...prev, dayEntry: entry }))
      if (selectedDate !== todayYmd) setSelectedDate(todayYmd)
    } catch (err: any) {
      if (err instanceof FieldGpsError) {
        if (err.code !== 'cancelled') alert(err.message)
        return
      }
      alert(t('punchInFailed', { message: err.message || t('unknownError', { ns: 'common' }) }))
    } finally {
      setPunchBusy(false)
    }
  }

  const handleComplete = async (job: MyDayJob) => {
    const pendingPhotos = await countQueuedPhotosForJob(job.id)
    if (pendingPhotos > 0) {
      alert(t('pendingPhotosUpload', { count: pendingPhotos }))
      setExpandedJobId(job.id)
      setStripChecklistOpen(true)
      return
    }
    const gate = await evaluateJobCompletionGate(job.id, photoPolicy)
    if (!gate.ok) {
      if (canExceptionComplete) {
        setExceptionGateMessage(gate.message || t('checklistOrPhotosIncomplete'))
        setExceptionJobId(job.id)
        setExpandedJobId(job.id)
        setStripChecklistOpen(true)
        return
      }
      alert(gate.message)
      setExpandedJobId(job.id)
      setStripChecklistOpen(true)
      return
    }
    setCompletionJobId(job.id)
  }

  const applyCompletion = async (jobId: string, exception?: { reason: string }) => {
    if (!employeeId) return
    try {
      setBusyJobId(jobId)
      const gated = await gateJobSiteAction({
        jobId,
        actionLabel: t('jobCompleteAction'),
        attemptType: 'job_complete',
      })
      const updateData: Record<string, unknown> = {
        status: resolvePostCompletionStatus('completed'),
        actual_end_time: new Date().toISOString(),
        ...jobCompleteGpsPatch(gated.position),
      }
      if (exception?.reason?.trim()) {
        const { data: auth } = await supabase.auth.getUser()
        Object.assign(updateData, completionExceptionUpdate(exception.reason, auth.user?.id))
      }
      const { error } = await supabase.from('jobs').update(updateData).eq('id', jobId)
      if (error) throw error

      const { state } = await enterTravelAfterJob({
        employeeId,
        jobId,
        geo: gated.geo,
        distanceFt: gated.distanceFt,
      })
      setClock(state)
      await stampJobHoursActualFromTimeEntries(jobId)

      const jobList = await loadJobs(employeeId, selectedDate)
      await refreshChecklistForJobs(jobList)
      await refreshBlockingJob(employeeId)

      if (selectedDate === todayDateInputValue()) {
        const remaining = jobList.filter(
          (j) => !isJobDoneStatus(j.status) && j.status !== 'cancelled'
        )
        if (remaining.length === 0) {
          lastJobPrompted.current = false
          await maybePromptAllJobsDone(state, true)
        }
      }
    } catch (err: any) {
      if (err instanceof FieldGpsError) {
        if (err.code !== 'cancelled') alert(err.message)
        return
      }
      alert(t('completeJobFailed', { message: err.message || t('unknownError', { ns: 'common' }) }))
    } finally {
      setBusyJobId(null)
    }
  }

  const doneCount = jobs.filter((j) => isJobDoneStatus(j.status)).length
  const inProgressCount = jobs.filter((j) => j.status === 'in_progress').length
  const yetToStartCount = jobs.filter(
    (j) => !isJobDoneStatus(j.status) && j.status !== 'in_progress' && j.status !== 'cancelled'
  ).length

  const inProgressStripJob =
    jobs.find((j) => j.status === 'in_progress' && j.id === clock.jobEntry?.job_id) ||
    jobs.find((j) => j.status === 'in_progress') ||
    todayJobs.find((j) => j.status === 'in_progress' && j.id === clock.jobEntry?.job_id) ||
    todayJobs.find((j) => j.status === 'in_progress') ||
    null

  const filteredJobs = jobs.filter((j) => jobMatchesStatusFilter(j, statusFilter))

  const selectStatusFilter = (key: JobStatusFilter) => {
    if (key !== 'total' && statusFilter === key) {
      setStatusFilter('total')
      return
    }
    setStatusFilter(key)
  }

  const shiftSelectedDate = (days: number) => {
    const next = addCalendarDays(selectedDate, days)
    if (next) setSelectedDate(next)
  }

  const selectJobFromMap = (jobId: string) => {
    setJobsView('list')
    setExpandedJobId(jobId)
    window.setTimeout(() => {
      document
        .getElementById(`my-day-job-${jobId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }

  const remainingJobs = jobs.filter(
    (j) => !isJobDoneStatus(j.status) && j.status !== 'cancelled'
  )

  const handleOptimizeRoute = async () => {
    if (remainingJobs.length === 0 || routeBusy) return
    try {
      setRouteBusy(true)
      const geo = await requireGpsCapture()
      const pos = geo.position
      if (!pos) return
      const stops = remainingJobs.map((j) => {
        const c = jobLatLng(j)
        return {
          id: j.id,
          latitude: c?.latitude ?? null,
          longitude: c?.longitude ?? null,
          eveningPreferred: isDsnyEveningJob({
            title: j.title,
            serviceType: j.service_type,
            dsnyIntegration: j.dsny_integration,
            dsnyPickupDays: j.dsny_pickup_days,
          }),
          scheduledStartTime: j.scheduled_start_time,
        }
      })
      if (!stops.some((s) => s.latitude != null && s.longitude != null)) {
        alert(t('noGpsForRoute'))
        return
      }
      const orderedIds = optimizeRouteOrder(stops, {
        latitude: pos.latitude,
        longitude: pos.longitude,
      })
      const byId = new Map(jobs.map((j) => [j.id, j]))
      const finished = jobs.filter(
        (j) => isJobDoneStatus(j.status) || j.status === 'cancelled'
      )
      const orderedRemaining = orderedIds
        .map((id) => byId.get(id))
        .filter(Boolean) as MyDayJob[]
      setJobs([...orderedRemaining, ...finished])
    } catch (err: any) {
      if (err instanceof FieldGpsError) {
        if (err.code !== 'cancelled') alert(err.message)
        return
      }
      alert(t('optimizeFailed', { message: err.message || t('unknownError', { ns: 'common' }) }))
    } finally {
      setRouteBusy(false)
    }
  }

  const toolbarToggleClass = (active: boolean) =>
    `inline-flex items-center justify-center gap-1 min-h-[36px] px-2.5 rounded-lg text-xs font-semibold transition-colors ${
      active
        ? 'bg-blue-600 text-white'
        : 'bg-white text-gray-600 border border-gray-200 active:bg-gray-50'
    }`

  const activeJobTitle = clock.jobEntry?.job_id
    ? (jobs.find((j) => j.id === clock.jobEntry!.job_id) ||
        todayJobs.find((j) => j.id === clock.jobEntry!.job_id))?.title
    : null

  const attendanceLabel = !clock.dayEntry
    ? t('readyToPunchIn')
    : clock.travelEntry
      ? t('travelingBetweenJobs')
      : activeJobTitle
        ? t('onJob', { title: activeJobTitle })
        : t('clockedIn')

  if (employeeLoading || !hasLoaded) {
    const spinner = <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
    if (embedded) {
      return <div className="flex items-center justify-center min-h-[200px]">{spinner}</div>
    }
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">{spinner}</MobilePage>
    )
  }

  const body = (
    <>
      {showHeader && (
        <div className={`${mobile.surface} p-4`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 tracking-tight truncate">{displayTitle}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{todayLabel}</p>
            </div>
            <span
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600"
              aria-hidden
            >
              <UserIcon className="w-5 h-5" />
            </span>
          </div>

          <div className="mt-3.5 pt-3.5 border-t border-gray-100 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                {clock.dayEntry ? (clock.travelEntry ? t('travel') : t('onTheClock')) : t('attendance')}
              </p>
              <p className="text-sm font-semibold text-gray-900 truncate mt-0.5">
                {attendanceLabel}
              </p>
            </div>
            {clock.dayEntry ? (
              <button
                type="button"
                onClick={handleClockOutDay}
                disabled={busyJobId !== null || punchBusy}
                className={`${mobile.dangerBtn} flex-shrink-0 rounded-full min-h-[44px] px-4 text-sm`}
              >
                <SquareIcon className="w-4 h-4" />
                {t('clockOut')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handlePunchIn}
                disabled={!employeeId || punchBusy || busyJobId !== null}
                className={`${mobile.primaryBtnPill} flex-shrink-0`}
              >
                {punchBusy ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  <PlayIcon className="w-4 h-4" />
                )}
                {t('punchIn')}
              </button>
            )}
          </div>
        </div>
      )}

      {inProgressStripJob && (
        <MyDayInProgressStrip
          job={inProgressStripJob}
          busy={busyJobId === inProgressStripJob.id}
          disabled={!employeeId}
          open={stripChecklistOpen}
          onToggle={() => setStripChecklistOpen((prev) => !prev)}
          onOpenDetails={() =>
            navigate(`/jobs/details/${inProgressStripJob.id}`, { state: { from: '/dashboard' } })
          }
          onComplete={() => void handleComplete(inProgressStripJob)}
        >
          <JobTaskChecklist
            jobId={inProgressStripJob.id}
            contractId={inProgressStripJob.contract_id}
            serviceType={inProgressStripJob.service_type}
            generalOnly
            flowOnly
            startPhotoMode={stripChecklistOpen}
            startPhotoModeNonce={photoModeNonceByJob[inProgressStripJob.id] || 0}
            onCompleteJob={() => void handleComplete(inProgressStripJob)}
            completeBusy={busyJobId === inProgressStripJob.id}
            onTasksChange={(tasks) => {
              const complete = isJobChecklistComplete(tasks)
              setChecklistCompleteByJob((prev) =>
                prev[inProgressStripJob.id] === complete
                  ? prev
                  : { ...prev, [inProgressStripJob.id]: complete }
              )
            }}
          />
        </MyDayInProgressStrip>
      )}

      {topSlot}

      <SharedMessagePacketsPanel userId={user?.id ?? null} />

      {!employeeId && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-800 text-sm">
          {t('employeeNotLinked')}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-2 mb-2 px-0.5 flex-wrap">
          <h2 className="text-base font-bold text-gray-900">
            {t('jobsCount', { count: jobs.length })}
          </h2>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setJobsView('map')}
              className={toolbarToggleClass(jobsView === 'map')}
              aria-pressed={jobsView === 'map'}
              aria-label={t('mapView')}
            >
              <MapIcon className="w-3.5 h-3.5" />
              {t('map')}
            </button>
            <button
              type="button"
              onClick={() => setJobsView('list')}
              className={toolbarToggleClass(jobsView === 'list')}
              aria-pressed={jobsView === 'list'}
              aria-label={t('listView')}
            >
              <ListIcon className="w-3.5 h-3.5" />
              {t('list')}
            </button>
            <button
              type="button"
              onClick={() => void handleOptimizeRoute()}
              disabled={remainingJobs.length === 0 || routeBusy}
              className={`${toolbarToggleClass(false)} disabled:opacity-40`}
              aria-label={t('optimizeRoute')}
              title={t('optimizeRoute')}
            >
              {routeBusy ? (
                <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <MapPinIcon className="w-3.5 h-3.5" />
              )}
              {t('optimize')}
            </button>
            <div className="flex items-center ml-0.5">
              <button
                type="button"
                onClick={() => shiftSelectedDate(-1)}
                className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] rounded-lg text-gray-600 active:bg-gray-100"
                aria-label={t('previousDay')}
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isViewingToday) setSelectedDate(todayYmd)
                }}
                className="min-w-[5.25rem] px-1 text-xs font-semibold text-gray-800 tabular-nums whitespace-nowrap"
                aria-label={isViewingToday ? t('today') : t('jumpToToday', { label: dayNavLabel })}
              >
                {dayNavLabel}
              </button>
              <button
                type="button"
                onClick={() => shiftSelectedDate(1)}
                className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] rounded-lg text-gray-600 active:bg-gray-100"
                aria-label={t('nextDay')}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <MobileStatStrip
          stats={[
            {
              key: 'total',
              label: t('stats.totalJobs'),
              value: jobs.length,
              fill: 'rose',
              pressed: statusFilter === 'total',
              onClick: () => selectStatusFilter('total'),
            },
            {
              key: 'yet',
              label: t('stats.yetToStart'),
              value: yetToStartCount,
              fill: 'amber',
              pressed: statusFilter === 'yet',
              onClick: () => selectStatusFilter('yet'),
            },
            {
              key: 'progress',
              label: t('stats.inProgress'),
              value: inProgressCount,
              fill: 'sky',
              pressed: statusFilter === 'progress',
              onClick: () => selectStatusFilter('progress'),
            },
            {
              key: 'done',
              label: t('stats.completed'),
              value: doneCount,
              fill: 'emerald',
              pressed: statusFilter === 'done',
              onClick: () => selectStatusFilter('done'),
            },
          ]}
        />
      </div>

      {jobsLoading && hasLoaded ? (
        <div className="flex items-center justify-center py-10">
          <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : jobs.length === 0 ? (
        <div className={mobile.surface}>
          <MobileEmptyState
            icon={<CalendarIcon className="w-12 h-12" />}
            title={isViewingToday ? t('empty.noJobsToday') : t('empty.noJobsOnDay', { label: dayNavLabel })}
            description={t('empty.noJobsHint')}
          />
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className={mobile.surface}>
          <MobileEmptyState
            icon={<CalendarIcon className="w-12 h-12" />}
            title={t('empty.noJobsInStatus')}
            description={t('empty.noJobsInStatusHint')}
          />
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredJobs.map((job) => {
            const isExpanded = expandedJobId === job.id
            const isBusy = busyJobId === job.id
            const isDone = isJobDoneStatus(job.status)
            const paused = isMyDayJobClockPaused(job.status, job.id, clock)
            const clockJobEntryJobDone =
              !!clock.jobEntry &&
              (!blockingJob || blockingJob.id !== clock.jobEntry.job_id)
            const workflow = resolveMyDayWorkflowButton({
              jobStatus: job.status,
              jobId: job.id,
              checklistComplete: !!checklistCompleteByJob[job.id],
              clock,
              blockingJobId: requireFinishBeforeStart ? blockingJob?.id ?? null : null,
              clockJobEntryJobDone,
              requireFinishBeforeStart,
            })

            return (
              <div key={job.id} id={`my-day-job-${job.id}`}>
              <MobileExpandable
                open={isExpanded}
                onToggle={() => {
                  const nextOpen = !isExpanded
                  setExpandedJobId(nextOpen ? job.id : null)
                  if (nextOpen && job.status === 'in_progress' && !paused) {
                    void fetchJobTasks(job.id).then((tasks) => {
                      setChecklistCompleteByJob((prev) => ({
                        ...prev,
                        [job.id]: isJobChecklistComplete(tasks),
                      }))
                    })
                  }
                }}
                onCardClick={() =>
                  navigate(`/jobs/details/${job.id}`, { state: { from: '/dashboard' } })
                }
                badges={
                  <div className="flex items-center justify-between gap-2 w-full">
                    <span className="text-xs font-semibold text-gray-400 tabular-nums">
                      #{job.job_number}
                    </span>
                    <MobileStatusBadge status={job.status} />
                  </div>
                }
                title={<span className="text-[15px] font-bold text-gray-900">{job.title}</span>}
                subtitle={
                  <>
                    <div className="flex items-center gap-1.5 text-gray-500">
                      <ClockIcon className="w-3.5 h-3.5 text-sky-500" />
                      {isViewingToday ? t('today') : dayNavLabel}, {formatTime(job.scheduled_start_time)} â€“{' '}
                      {formatTime(job.scheduled_end_time)}
                    </div>
                    <div className="flex items-center gap-1.5 truncate text-gray-600">
                      <UserIcon className="w-3.5 h-3.5 text-blue-500" />
                      {getCustomerName(job)}
                    </div>
                    <div className="flex items-center gap-1.5 truncate text-gray-500">
                      <MapPinIcon className="w-3.5 h-3.5 text-emerald-500" />
                      {job.property?.name || job.property?.address || naLabel}
                    </div>
                    {paused && (
                      <p className="text-xs text-amber-700 mt-0.5">
                        {t('pausedClockOut')}
                      </p>
                    )}
                  </>
                }
                actions={
                  workflow ? (
                    <button
                      type="button"
                      disabled={isBusy || !employeeId || !!workflow.disabled}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (workflow.action === 'start' || workflow.action === 'resume') {
                          void handleStart(job)
                        } else if (workflow.action === 'checklist') {
                          setExpandedJobId(job.id)
                          setPhotoModeNonceByJob((prev) => ({
                            ...prev,
                            [job.id]: (prev[job.id] || 0) + 1,
                          }))
                          void fetchJobTasks(job.id).then((tasks) => {
                            setChecklistCompleteByJob((prev) => ({
                              ...prev,
                              [job.id]: isJobChecklistComplete(tasks),
                            }))
                          })
                        } else if (workflow.action === 'complete') void handleComplete(job)
                      }}
                      className={
                        workflow.disabled
                          ? 'inline-flex items-center justify-center gap-2 text-sm font-semibold min-h-[44px] px-4 rounded-full w-full sm:w-auto bg-gray-200 text-gray-400 cursor-not-allowed pointer-events-none'
                          : `${
                              workflow.variant === 'success' ? mobile.successBtn : mobile.primaryBtn
                            } text-sm min-h-[44px] px-4 rounded-full w-full sm:w-auto`
                      }
                    >
                      {isBusy ? (
                        <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
                      ) : workflow.action === 'start' || workflow.action === 'resume' ? (
                        <PlayIcon className="w-3.5 h-3.5" />
                      ) : workflow.action === 'checklist' ? (
                        <ClipboardListIcon className="w-3.5 h-3.5" />
                      ) : (
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                      )}
                      {t(`workflow.${workflow.action}`)}
                    </button>
                  ) : isDone ? (
                    <span className="text-sm font-semibold text-emerald-700">{t("completed")}</span>
                  ) : null
                }
              >
                <ApartmentJobWork jobId={job.id} serviceType={job.service_type} />
                {(!inProgressStripJob || job.id !== inProgressStripJob.id) && (
                  <JobTaskChecklist
                    jobId={job.id}
                    contractId={job.contract_id}
                    serviceType={job.service_type}
                    generalOnly
                    flowOnly={job.status === 'in_progress'}
                    startPhotoModeNonce={photoModeNonceByJob[job.id] || 0}
                    onCompleteJob={
                      job.status === 'in_progress' ? () => void handleComplete(job) : undefined
                    }
                    completeBusy={busyJobId === job.id}
                    onTasksChange={(tasks) => {
                      const complete = isJobChecklistComplete(tasks)
                      setChecklistCompleteByJob((prev) =>
                        prev[job.id] === complete ? prev : { ...prev, [job.id]: complete }
                      )
                    }}
                  />
                )}
              </MobileExpandable>
              </div>
            )
          })}
        </div>
      )}

      {completionJobId &&
        (canSeePricing ? (
          <JobCompletionModal
            jobId={completionJobId}
            isOpen={true}
            autoHours={fieldHome}
            onClose={() => setCompletionJobId(null)}
            onSaved={async () => {
              const jobId = completionJobId
              setCompletionJobId(null)
              if (jobId) await applyCompletion(jobId)
            }}
          />
        ) : (
          <FieldJobCompletionModal
            jobId={completionJobId}
            isOpen={true}
            onClose={() => setCompletionJobId(null)}
            onSaved={async () => {
              const jobId = completionJobId
              setCompletionJobId(null)
              if (jobId) await applyCompletion(jobId)
            }}
          />
        ))}

      <CompletionExceptionModal
        open={!!exceptionJobId}
        gateMessage={exceptionGateMessage}
        busy={!!busyJobId}
        onCancel={() => {
          setExceptionJobId(null)
          setExceptionGateMessage(null)
        }}
        onConfirm={async (reason) => {
          if (!exceptionJobId) return
          const jobId = exceptionJobId
          setExceptionJobId(null)
          setExceptionGateMessage(null)
          await applyCompletion(jobId, { reason })
        }}
      />

      <PunchInJobPickerSheet
        open={punchInPickerOpen}
        jobs={isViewingToday ? remainingJobs : openJobsRemaining}
        busy={punchBusy}
        onClose={() => setPunchInPickerOpen(false)}
        onSelectJob={(job) => void confirmDayPunchIn({ job })}
        onSelectOther={(reason) => void confirmDayPunchIn({ otherNotes: reason })}
      />

      <UnfinishedJobClockOutModal
        open={!!clockOutPromptJob}
        job={clockOutPromptJob}
        busy={punchBusy}
        onCancel={() => setClockOutPromptJob(null)}
        onGoFinish={() => {
          const job = clockOutPromptJob
          setClockOutPromptJob(null)
          if (!job) return
          setExpandedJobId(job.id)
          const onList = jobs.find((j) => j.id === job.id)
          if (onList) {
            // Scroll into view via expand; Resume is on the card
            return
          }
          navigate(`/jobs/details/${job.id}`, { state: { from: '/dashboard' } })
        }}
        onLeaveUnfinished={doClockOutDay}
      />

      {jobsView === 'map' && (
        <JobsDayMap
          jobs={remainingJobs}
          onClose={() => setJobsView('list')}
          onSelectJob={selectJobFromMap}
        />
      )}
    </>
  )

  if (embedded) return <div className="space-y-3.5">{body}</div>
  return <MobilePage>{body}</MobilePage>
}
